import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

import BoxContainer from '../../../style-components/BoxContainer.jsx';
import Loading from '../../../style-components/Loading.jsx';
import HeaderContainer from '../../../style-components/HeaderContainer.jsx';
import PrimaryButton from '../../Custom-UI/Button.jsx';

import { RESOURCE_TYPES } from '../constants.js';

import ResourceSelectionModal from './components/ResourceSelectionModal.jsx';
import UnitResourcesCard from './components/UnitResourcesCard.jsx';

import { useGetCourse } from '../../../models/course.js';
import { useGetCourseLearningUnits, useGetLearnunits } from '../../../models/learnunits.js';

const DEFAULT_TYPE = RESOURCE_TYPES[0].key;

function LearningPathForm({ mode = 'create', initialData = null, submitting, onSubmit }) {
  const navigate = useNavigate();
  const { id: courseId } = useParams();

  const [course] = useGetCourse(courseId);
  const [courseLearnunitsData, courseLearnunitsLoading] = useGetCourseLearningUnits(courseId);
  const [learnunitsNamesData, learnunitsNamesLoading] = useGetLearnunits();

  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');

  // Único dato del formulario que no son los campos de texto:
  // { [unitId]: { [resource_type]: [{ key, path, name, thumbnail }] } }
  const [selections, setSelections] = useState(initialData?.selections || {});

  // Unidad + tipo que está abierta en el modal del catálogo; null = cerrado.
  const [picker, setPicker] = useState(null);

  // Rastro de carpetas por unidad + tipo. Vive acá, y no en el modal, para que
  // reabrir el catálogo caiga en la carpeta donde lo habías dejado.
  const [segmentsByKey, setSegmentsByKey] = useState({});

  // Une las unidades del curso con sus nombres del catálogo global.
  const units = useMemo(() => {
    const courseLearnunits = courseLearnunitsData?.data || [];
    const namesList = learnunitsNamesData?.data || [];
    const namesDict = {};
    namesList.forEach(item => {
      namesDict[item.id] = item.name;
    });
    return courseLearnunits.map(cl => ({
      learnunit_id: cl.learnunit_id,
      name: namesDict[cl.learnunit_id] || `Unidad ${cl.learnunit_id}`,
    }));
  }, [courseLearnunitsData, learnunitsNamesData]);

  const dataLoading = courseLearnunitsLoading || learnunitsNamesLoading;

  const totalSelected = useMemo(
    () =>
      Object.values(selections).reduce(
        (unitAcc, unitSel) =>
          unitAcc + Object.values(unitSel).reduce((typeAcc, list) => typeAcc + list.length, 0),
        0
      ),
    [selections]
  );

  const rsKey = (unitId, type) => `${unitId}:${type}`;

  const getSegments = (unitId, type) => segmentsByKey[rsKey(unitId, type)] || [];

  const openPicker = (unitId, type) => setPicker({ unitId, type });

  const closePicker = () => setPicker(null);

  const drillDownResource = item => {
    if (!picker) return;
    const key = rsKey(picker.unitId, picker.type);
    setSegmentsByKey(prev => ({ ...prev, [key]: [...(prev[key] || []), item.id] }));
  };

  const goBackResource = () => {
    if (!picker) return;
    const key = rsKey(picker.unitId, picker.type);
    setSegmentsByKey(prev => ({ ...prev, [key]: (prev[key] || []).slice(0, -1) }));
  };

  // Alterna la selección de una hoja del catálogo. Guarda `path` (el rastro
  // completo, para reconstruir la ruta de detalle) y el nombre y la miniatura,
  // que son lo que muestra la tarjeta de la unidad sin volver a pedir nada.
  const toggleResource = item => {
    if (!picker) return;
    const { unitId, type } = picker;
    const itemId = String(item.id);
    const path = [...getSegments(unitId, type), itemId];
    setSelections(prev => {
      const unitSel = prev[unitId] || {};
      const current = unitSel[type] || [];
      const exists = current.some(entry => entry.key === itemId);
      const next = exists
        ? current.filter(entry => entry.key !== itemId)
        : [...current, { key: itemId, path, name: item.title, thumbnail: item.image }];
      return { ...prev, [unitId]: { ...unitSel, [type]: next } };
    });
  };

  const removeResource = (unitId, type, key) => {
    setSelections(prev => {
      const unitSel = prev[unitId] || {};
      return {
        ...prev,
        [unitId]: { ...unitSel, [type]: (unitSel[type] || []).filter(entry => entry.key !== key) },
      };
    });
  };

  const pickerUnit = picker ? units.find(unit => unit.learnunit_id === picker.unitId) : null;
  const pickerSegments = picker ? getSegments(picker.unitId, picker.type) : [];

  const pickerCountByType = RESOURCE_TYPES.reduce((acc, type) => {
    acc[type.key] = picker ? (selections[picker.unitId]?.[type.key] || []).length : 0;
    return acc;
  }, {});

  // Solo las hojas seleccionadas EN EL NIVEL actual del catálogo: `entry.path`
  // guarda el rastro completo, así que comparar la carpeta padre evita marcar
  // como seleccionado un id igual que viva en otra carpeta.
  const pickerSelectedKeys = picker
    ? (selections[picker.unitId]?.[picker.type] || [])
        .filter(entry => (entry.path || []).slice(0, -1).join('/') === pickerSegments.join('/'))
        .map(entry => entry.key)
    : [];

  // Arma el payload en el formato que espera el backend: cada recurso lleva
  // `items` = [{ key, path }]. Se envían como strings (key y cada segmento del
  // path) para que la ruta de detalle y la validación del backend sean estables.
  const buildLearningUnitsData = () =>
    units
      .map(unit => {
        const unitSel = selections[unit.learnunit_id] || {};
        const resources = RESOURCE_TYPES.map(type => {
          const entries = unitSel[type.key] || [];
          return {
            resource_type: type.key,
            items: entries.map(entry => ({
              key: String(entry.key),
              path: (entry.path || []).map(String),
            })),
          };
        }).filter(resource => resource.items.length > 0);
        return { learnunit_id: unit.learnunit_id, resources };
      })
      .filter(unit => unit.resources.length > 0);

  const handleSubmit = async () => {
    if (!title.trim()) {
      alert('El título es obligatorio.');
      return;
    }

    const learningUnitsData = buildLearningUnitsData();

    if (learningUnitsData.length === 0) {
      alert('Debes agregar al menos un recurso a la ruta de aprendizaje.');
      return;
    }

    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      learningUnitsData,
    };

    await onSubmit(payload);
  };

  const breadcrumb = {
    items: [
      { name: 'Cursos', href: '/courses' },
      { name: course?.name ?? 'Curso', href: '/courses' },
      { name: 'Rutas de Aprendizaje', href: `/courses/${courseId}/learning-paths` },
      { name: mode === 'create' ? 'Nueva Ruta' : 'Editar Ruta', href: '' },
    ],
    maxItems: 4,
  };

  return (
    <BoxContainer>
      <div className='antialiased font-sans w-full'>
        <div className='divide-y divide-primary'>
          <HeaderContainer breadcrumb={breadcrumb} title='Rutas de Aprendizaje' />
          <div className='py-8 px-4 sm:px-8'>
            <h1 className='mb-8 text-2xl font-bold text-primary'>
              {mode === 'create' ? 'Nueva Ruta' : 'Editar Ruta'}
            </h1>

            {/* Título */}
            <div className='mb-6'>
              <label className='mb-2 block font-semibold text-primary text-left'>Título</label>
              <input
                type='text'
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder='Ej: Introducción al sistema óseo'
                className='w-full rounded-lg border border-border bg-white px-4 py-3 text-primary placeholder-muted-foreground focus:border-secondary focus:outline-none'
              />
            </div>

            {/* Descripción */}
            <div className='mb-8'>
              <label className='mb-2 block font-semibold text-primary text-left'>Descripción</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                placeholder='Describe el objetivo y alcance de esta ruta de aprendizaje...'
                className='w-full resize-y rounded-lg border border-border bg-white px-4 py-3 text-primary placeholder-muted-foreground focus:border-secondary focus:outline-none'
              />
            </div>

            {/* Recursos por unidad */}
            <div className='mb-4 flex flex-wrap items-end justify-between gap-2'>
              <div>
                <h2 className='text-lg font-semibold text-primary text-left'>
                  Recursos por Unidad de Aprendizaje
                </h2>
                <p className='mt-1 text-sm text-muted-foreground text-left'>
                  Agrega imágenes, videos y estructuras anatómicas a cada unidad del curso.
                </p>
              </div>
              <span className='rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground'>
                {totalSelected} {totalSelected === 1 ? 'recurso' : 'recursos'} en total
              </span>
            </div>

            {dataLoading ? (
              <div className='flex justify-center py-10'>
                <Loading />
              </div>
            ) : units.length === 0 ? (
              <div className='rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground'>
                Este curso no tiene unidades de aprendizaje.
              </div>
            ) : (
              <div className='flex flex-col gap-4'>
                {units.map((unit, index) => (
                  <UnitResourcesCard
                    key={unit.learnunit_id}
                    unit={unit}
                    index={index}
                    selectionsByType={selections[unit.learnunit_id]}
                    onAdd={type => openPicker(unit.learnunit_id, type)}
                    onRemove={(type, key) => removeResource(unit.learnunit_id, type, key)}
                  />
                ))}
              </div>
            )}

            {/* Acciones */}
            <div className='mt-8 flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => navigate(`/courses/${courseId}/learning-paths`)}
                className='rounded-full bg-muted px-6 py-2 font-semibold text-primary transition duration-150 hover:bg-muted-foreground/20'>
                Cancelar
              </button>
              <PrimaryButton
                variant='contained'
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={<SendOutlinedIcon />}>
                {mode === 'create' ? 'Crear Ruta de Aprendizaje' : 'Guardar Cambios'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>

      <ResourceSelectionModal
        open={Boolean(picker)}
        onClose={closePicker}
        unitName={pickerUnit?.name ?? ''}
        activeType={picker?.type ?? DEFAULT_TYPE}
        pathSegments={pickerSegments}
        onDrillDown={drillDownResource}
        onBack={goBackResource}
        selectedKeys={pickerSelectedKeys}
        onSelect={toggleResource}
        countByType={pickerCountByType}
      />
    </BoxContainer>
  );
}

export default LearningPathForm;
